import { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, Briefcase, Handshake, Landmark, Users, Scale } from "lucide-react";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { COMPANY, ENQUIRY_TYPES, SUBSIDIARIES } from "@/lib/content";
import { api } from "@/lib/api";

const ENQUIRY_ICONS = { Briefcase, Handshake, Landmark, Users, Scale };

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();

  // Pre-fill subject from ?subject= query param (e.g. /contact?subject=salihi-petroleum)
  useEffect(() => {
    const q = new URLSearchParams(location.search).get("subject");
    if (!q) return;
    const sub = SUBSIDIARIES.find((s) => s.slug === q);
    setForm((f) => ({ ...f, subject: sub ? `Enquiry about ${sub.name}` : q }));
  }, [location.search]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact/submit", form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch (_e) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const mapUrl = `https://www.google.com/maps?q=${COMPANY.mapLat},${COMPANY.mapLng}&hl=en&z=16&output=embed`;

  return (
    <PageShell
      testId="contact-page"
      title="Contact Salihi Group — Mogadishu, Somalia"
      description={`Contact Salihi Group. Headquartered near Aden Abdulle International Airport, Mogadishu, Somalia. Phone ${COMPANY.phone}.`}
      eyebrow="Contact"
      heading={<>Get in <em className="text-gold not-italic">touch</em>.</>}
      subheading="We respond to every enquiry within one business day."
      breadcrumbs={[{ label: "Contact" }]}
    >
      {/* How We Can Help */}
      <section className="mb-16">
        <div className="max-w-3xl mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="gold-hairline" />
            <span className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium">How We Can Help</span>
          </div>
          <h2 className="font-serif text-navy text-2xl md:text-3xl font-light mb-4">
            Five common reasons organisations reach out.
          </h2>
          <p className="text-mutedink text-[15px] leading-relaxed font-light">
            Enquiries are handled by the Salihi Group team and routed to the appropriate subsidiary. We aim to
            respond to all messages promptly during business hours.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {ENQUIRY_TYPES.map((e, i) => {
            const Icon = ENQUIRY_ICONS[e.icon] || Briefcase;
            return (
              <article key={e.title} data-testid={`enquiry-type-${i}`}
                       className="bg-white border border-softgray p-5 hover:border-gold transition-colors">
                <Icon size={20} strokeWidth={1.5} className="text-gold mb-3" />
                <div className="text-navy font-serif text-[15px] leading-tight mb-2">{e.title}</div>
                <div className="text-mutedink text-[13px] font-light leading-relaxed">{e.body}</div>
              </article>
            );
          })}
        </div>
      </section>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Info + Map */}
        <aside className="lg:col-span-5 space-y-8">
          <div className="bg-white border border-softgray p-8 md:p-10">
            <h3 className="font-serif text-navy text-xl mb-6">Salihi Group Head Office</h3>
            <ul className="space-y-5 text-[15px]">
              <li className="flex gap-4">
                <MapPin size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <span className="text-navy leading-relaxed">{COMPANY.address}</span>
              </li>
              <li className="flex gap-4">
                <Phone size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="text-navy hover:text-gold" data-testid="contact-page-phone">{COMPANY.phone}</a>
              </li>
              <li className="flex gap-4">
                <Mail size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <a href={`mailto:${COMPANY.email}`} className="text-navy hover:text-gold" data-testid="contact-page-email">{COMPANY.email}</a>
              </li>
              <li className="flex gap-4">
                <Clock size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <span className="text-navy">{COMPANY.hours}</span>
              </li>
            </ul>
          </div>

          <div className="aspect-[4/3] w-full border border-softgray overflow-hidden">
            <iframe
              title="Salihi Group location map"
              src={mapUrl}
              width="100%"
              height="100%"
              loading="lazy"
              style={{ border: 0, filter: "grayscale(30%) contrast(1.05)" }}
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-page-map"
            />
          </div>
        </aside>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="bg-white border border-softgray p-8 md:p-10" data-testid="contact-page-form">
            {submitted ? (
              <div className="text-center py-10" data-testid="contact-success">
                <CheckCircle2 size={48} strokeWidth={1.5} className="text-gold mx-auto mb-4" />
                <h3 className="font-serif text-navy text-2xl">Thank you</h3>
                <p className="text-mutedink mt-3">Your message has been received. A member of our team will be in touch shortly.</p>
                <button onClick={() => setSubmitted(false)} className="btn-outline-gold mt-6 text-xs">Send Another</button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" required value={form.name} onChange={update("name")} testId="contact-page-name" />
                <Field label="Email" type="email" required value={form.email} onChange={update("email")} testId="contact-page-email-input" />
                <Field label="Phone" value={form.phone} onChange={update("phone")} testId="contact-page-phone-input" />
                <Field label="Company / Organization" value={form.company} onChange={update("company")} testId="contact-page-company" />
                <div className="sm:col-span-2"><Field label="Subject" value={form.subject} onChange={update("subject")} testId="contact-page-subject" /></div>
                <div className="sm:col-span-2">
                  <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">
                    Message <span className="text-gold">*</span>
                  </label>
                  <textarea
                    required rows={6} value={form.message} onChange={update("message")}
                    data-testid="contact-page-message"
                    className="w-full border border-softgray px-4 py-3 text-navy font-sans text-[15px] outline-none focus:border-gold transition-colors resize-y"
                  />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <button type="submit" disabled={submitting} data-testid="contact-page-submit"
                          className="btn-gold w-full sm:w-auto justify-center">
                    {submitting ? "Sending…" : (<>Send Message <Send size={14} strokeWidth={2} /></>)}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </PageShell>
  );
}

function Field({ label, type = "text", value, onChange, required, testId }) {
  return (
    <div>
      <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </label>
      <input
        type={type} required={required} value={value} onChange={onChange} data-testid={testId}
        className="w-full border border-softgray px-4 py-3 text-navy font-sans text-[15px] outline-none focus:border-gold transition-colors"
      />
    </div>
  );
}
